import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '@/shared/config/brand';
import { legalConfig } from '@/shared/config/legal';

const consentLabelId = 'sms-otp-consent-label';

interface SmsConsentFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  /** Tighter surface styling for dialogs. */
  compact?: boolean;
}

/**
 * Explicit, unchecked-by-default SMS consent for transactional OTP messages.
 * Required for Twilio A2P authentication campaigns.
 */
export function SmsConsentField<T extends FieldValues>({
  control,
  name,
  compact = false,
}: SmsConsentFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl
          error={!!fieldState.error}
          sx={{ mb: compact ? 1.75 : 2, width: '100%', alignItems: 'stretch' }}
        >
          <Box
            sx={{
              border: '1px solid',
              borderColor: fieldState.error ? 'error.light' : 'divider',
              borderRadius: 2,
              bgcolor: compact ? 'rgba(15, 23, 42, 0.02)' : 'background.default',
              px: compact ? 1.25 : 1.5,
              py: compact ? 1 : 1.25,
              transition: 'border-color 160ms ease, background-color 160ms ease',
              '&:hover': {
                borderColor: fieldState.error ? 'error.main' : 'rgba(37, 99, 235, 0.35)',
              },
            }}
          >
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
                mx: 0,
                gap: 0.75,
                width: '100%',
                '& .MuiCheckbox-root': {
                  p: 0,
                  mt: '1px',
                  color: 'text.secondary',
                },
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  minWidth: 0,
                },
              }}
              control={
                <Checkbox
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  size="small"
                  color="primary"
                  inputProps={{
                    'aria-labelledby': consentLabelId,
                  }}
                />
              }
              label={
                <Typography
                  id={consentLabelId}
                  component="span"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: compact ? '0.75rem' : '0.8125rem',
                    lineHeight: 1.55,
                    letterSpacing: '0.01em',
                  }}
                >
                  I agree to receive one-time verification codes from {brand.uiName} via SMS for
                  account authentication and security verification. Message frequency varies based
                  on my activity. Message and data rates may apply. Reply STOP to opt out and HELP
                  for help. See our{' '}
                  <Box
                    component={RouterLink}
                    to={legalConfig.privacyPolicyPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    Privacy Policy
                  </Box>{' '}
                  and{' '}
                  <Box
                    component={RouterLink}
                    to={legalConfig.termsPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    Terms &amp; Conditions
                  </Box>
                  .
                </Typography>
              }
            />
          </Box>
          {fieldState.error?.message && (
            <FormHelperText sx={{ mx: 0.5, mt: 0.75 }}>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

/** Plain-text consent copy for docs / Twilio campaign descriptions (links omitted). */
export const SMS_CONSENT_PLAIN_TEXT = `I agree to receive one-time verification codes from trplin via SMS for account authentication and security verification. Message frequency varies based on my activity. Message and data rates may apply. Reply STOP to opt out and HELP for help. See our Privacy Policy and Terms & Conditions.`;
